import type { FC, ReactNode } from "react";
import { useMemo } from "react";
import { useLocation } from "react-router";
import { Bell01, LifeBuoy01, SearchLg, Settings01 } from "@untitledui/icons";
import { BadgeWithDot } from "@/components/base/badges/badges";
import { Input } from "@/components/base/input/input";
import { cx } from "@/utils/cx";
import { activityService } from "@/data/mock-activities";
import { MobileNavigationHeader } from "./base-components/mobile-header";
import { NavItemBase } from "./base-components/nav-item";
import { NavItemButton } from "./base-components/nav-item-button";
import { NavList } from "./base-components/nav-list";

type NavItem = {
    /** Label text for the nav item. */
    label: string;
    /** URL to navigate to when the nav item is clicked. */
    href: string;
    /** Whether the nav item is currently active. */
    current?: boolean;
    /** Icon component to display. */
    icon?: FC<{ className?: string }>;
    /** Badge to display. */
    badge?: ReactNode;
    /** List of sub-items to display. */
    items?: NavItem[];
};

interface HeaderNavigationBaseProps {
    /** URL of the currently active item. */
    activeUrl?: string;
    /** List of items to display. */
    items: NavItem[];
    /** List of sub-items to display. */
    subItems?: NavItem[];
    /** Content to display in the trailing position. */
    trailingContent?: ReactNode;
    /** Whether to show the avatar dropdown. */
    showAvatarDropdown?: boolean;
    /** Whether to hide the bottom border. */
    hideBorder?: boolean;
}

export const HeaderNavigationBase = ({
    activeUrl,
    items,
    subItems,
    trailingContent,
    hideBorder = false,
}: HeaderNavigationBaseProps) => {
    const location = useLocation();
    const activeSubNavItems = subItems || items.find((item) => item.current && item.items && item.items.length > 0)?.items;

    const showSecondaryNav = activeSubNavItems && activeSubNavItems.length > 0;

    // Determine activity URL based on current path
    const isShipper = location.pathname.startsWith("/shipper") || location.pathname.startsWith("/sender");
    const isReceiver = location.pathname.startsWith("/receiver");
    const isFreightForwarder = location.pathname.startsWith("/freight-forwarder");

    const activityUrl = isShipper
        ? "/shipper/activity"
        : isReceiver
            ? "/receiver/activity"
            : isFreightForwarder
                ? "/freight-forwarder/activity"
                : "/notifications-01";

    // Determine settings URL based on current path
    const settingsUrl = isShipper
        ? "/shipper/settings"
        : isReceiver
            ? "/receiver/settings"
            : "#";

    // Determine audience and count unread notifications
    const audience = useMemo(() => {
        if (isShipper) return "shipper" as const;
        if (isReceiver) return "receiver" as const;
        if (isFreightForwarder) return "freightForwarder" as const;
        return null;
    }, [isShipper, isReceiver, isFreightForwarder]);

    const unreadCount = useMemo(() => {
        if (!audience) return 0;
        const activities = activityService.getByAudience(audience);
        return activities.filter((a) => !a.isRead).length;
    }, [audience]);


    return (
        <>
            <MobileNavigationHeader>
                <aside className="flex h-full max-w-full flex-col justify-between overflow-auto border-r border-secondary bg-primary pt-4 lg:pt-6">
                    <div className="flex flex-col gap-5 px-4 lg:px-5">
                        <h3 className="text-2xl font-bold text-primary">Eve Mind</h3>
                        <Input shortcut size="sm" aria-label="Search" placeholder="Search" icon={SearchLg} />
                    </div>

                    <NavList items={items} />

                    <div className="mt-auto flex flex-col gap-4 px-2 py-4 lg:px-4 lg:py-6">
                        <div className="flex flex-col gap-1">
                            <NavItemBase type="link" href="#" icon={LifeBuoy01}>
                                Support
                            </NavItemBase>
                            <NavItemBase
                                type="link"
                                href="#"
                                icon={Settings01}
                                badge={
                                    <BadgeWithDot color="success" type="modern" size="sm">
                                        Online
                                    </BadgeWithDot>
                                }
                            >
                                Settings
                            </NavItemBase>
                            <NavItemBase type="link" href="https://www.untitledui.com/" icon={Settings01}>
                                Open in browser
                            </NavItemBase>
                        </div>
                    </div>
                </aside>
            </MobileNavigationHeader>

            <header className="max-lg:hidden">
                <section
                    className={cx(
                        "flex h-16 w-full items-center justify-center bg-primary md:h-18",
                        (!hideBorder || showSecondaryNav) && "border-b border-secondary",
                    )}
                >
                    <div className="flex w-full max-w-container justify-between pr-3 pl-4 md:px-8">
                        <div />

                        <div className="flex items-center gap-3">
                            {trailingContent}

                            <div className="flex gap-0.5">
                                {!isFreightForwarder && (
                                    <NavItemButton
                                        current={activeUrl === settingsUrl}
                                        size="md"
                                        icon={Settings01}
                                        label="Settings"
                                        href={settingsUrl}
                                        tooltipPlacement="bottom"
                                    />
                                )}
                                <div className="relative">
                                    <NavItemButton
                                        current={activeUrl === activityUrl}
                                        size="md"
                                        icon={Bell01}
                                        label="Notifications"
                                        href={activityUrl}
                                        tooltipPlacement="bottom"
                                    />
                                    {unreadCount > 0 && (
                                        <span className="absolute -right-0.5 -bottom-0.5 flex h-2 w-2 items-center justify-center rounded-full bg-error-600 ring-1 ring-primary">
                                            <span className="sr-only">{unreadCount} notifiche non lette</span>
                                        </span>
                                    )}
                                </div>
                            </div>

                        </div>
                    </div>
                </section>

                {showSecondaryNav && (
                    <section className={cx("flex h-16 w-full items-center justify-center bg-primary", !hideBorder && "border-b border-secondary")}>
                        <div className="flex w-full max-w-container items-center justify-between gap-8 px-8">
                            <nav>
                                <ul className="flex items-center gap-0.5">
                                    {activeSubNavItems.map((item) => (
                                        <li key={item.label} className="py-0.5">
                                            <NavItemBase icon={item.icon} href={item.href} current={item.current} badge={item.badge} type="link">
                                                {item.label}
                                            </NavItemBase>
                                        </li>
                                    ))}
                                </ul>
                            </nav>

                            <Input shortcut aria-label="Search" placeholder="Search" icon={SearchLg} size="sm" className="max-w-xs" />
                        </div>
                    </section>
                )}
            </header>
        </>
    );
};
